/**
 * Phase 5.9 — Presence heartbeat hook.
 *
 * While the user is authenticated AND the tab is visible, POST a heartbeat
 * to /api/presence/heartbeat every PRESENCE_HEARTBEAT_INTERVAL ms. This is
 * what makes the dispatcher's "🟢 ONLINE" dot accurate.
 *
 * Cadence: 45s (half of the backend's 120s online window — one missed beat
 * is forgiving, two means truly offline).
 *
 * Pauses on:
 *   - document.hidden (background tab) — saves battery
 *   - not authenticated
 *   - unmount
 */
import { useEffect, useRef } from 'react';
import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const HEARTBEAT_MS = 45_000;

function getSessionId() {
  try {
    let id = sessionStorage.getItem('am.events.session_id');
    if (!id) {
      id = `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem('am.events.session_id', id);
    }
    return id;
  } catch (_e) {
    return null;
  }
}

export function usePresenceHeartbeat(isAuthed) {
  const timerRef = useRef(null);

  useEffect(() => {
    if (!isAuthed) return undefined;

    let cancelled = false;

    const beat = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        await axios.post(
          `${API_URL}/api/presence/heartbeat`,
          { session_id: getSessionId() },
          { withCredentials: true, timeout: 6000 },
        );
      } catch (_e) {
        // Swallow — presence is best-effort. Don't surface errors.
      }
    };

    // Fire immediately on mount + after every interval
    beat();
    timerRef.current = setInterval(beat, HEARTBEAT_MS);

    // Wake-up beat the moment the tab becomes visible again
    const onVis = () => { if (!document.hidden) beat(); };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [isAuthed]);
}
